// Extracted sph_progressive_noise_5 SPH solver (Phase 6). Reuses the main-tree
// util/kdtree; the point-record strides are this slice's own (imported from
// ./const.js) rather than the global window.P* the original read.
import * as util from "../util.js";
import * as kdtree from "../kdtree.js";
import cconst from "./const.js";

type ConfigBag = Record<string, any>;

// PTOT/PGEN/PR/PCLR/POLDX/POLDY/PDX/PDY/POGEN are read as the MAIN PointRecord
// globals (window.P*, set by the main const.ts) — that is the layout smoothedmask
// feeds in. Only PSTARTX/PSTARTY are sph5-specific; the original left them
// undefined when main loaded first (harmless no-op writes). smoothedmask sets
// window.PSTARTX=POX(16), PSTARTY=POY(17), so match those scratch offsets.
const PSTARTX = 16;
const PSTARTY = 17;

const gridoffs = cconst.gridoffs as number[][];

function torusPointDist(x1: number, y1: number, x2: number, y2: number): number {
  let dx = Math.min(Math.abs(x1 - x2), Math.abs(x1 - x2 + 1));
  dx = Math.min(dx, Math.abs(x1 - x2 - 1));
  let dy = Math.min(Math.abs(y1 - y2), Math.abs(y1 - y2 + 1));
  dy = Math.min(dy, Math.abs(y1 - y2 - 1));

  return Math.sqrt(dx * dx + dy * dy);
}

window.torusPointDist = torusPointDist;

class Solver {
  sph: SPH;
  params: ConfigBag | undefined;
  ranges: unknown;
  points: number[];

  constructor(sph: SPH, params?: ConfigBag, ranges?: unknown) {
    this.sph = sph;
    this.params = params;
    this.ranges = ranges;
    this.points = [];
  }

  calcDistance(ctx: ConfigBag, points: number[], lvl: number): number {
    const ps1 = this.points;
    const ps2 = points;
    let sum = 0.0;

    for (let pi = 0; pi < ps1.length; pi += PTOT) {
      sum += torusPointDist(ps1[pi], ps1[pi + 1], ps2[pi], ps2[pi + 1]);
    }

    return sum;
  }

  calcMaskError(ctx: ConfigBag, origpoints: number[]): number {
    const range = ctx.SOLVER_RANGE;
    ctx = ctx.copy();

    let error = 0.0;

    let time = util.time_ms();

    for (let i = 0; i < range; i++) {
      if (util.time_ms() - time > 700) {
        console.log("step", i + 1, "of", range);
        time = util.time_ms();
      }

      const gen = i / (range - 1);

      this.loadPoints(origpoints);
      this.makeLevelUniform(ctx, gen);

      let totpoints = 0;
      for (let pi = 0; pi < origpoints.length; pi += PTOT) {
        totpoints += +(origpoints[pi + PGEN] <= gen);
      }

      error += this.calcDistance(ctx, origpoints, gen) / (1 + totpoints);
    }

    return ((error / origpoints.length) * 10000000) / PTOT;
  }

  loadPoints(points: number[]): this {
    this.points = points.slice(0, points.length);
    return this;
  }

  unloadPoints(points: number[]): number[] {
    points.length = this.points.length;

    for (let i = 0; i < points.length; i++) {
      points[i] = this.points[i];
    }

    return points;
  }

  levelSPHStep(ctx: ConfigBag): void {
    ctx = ctx.copy();
    ctx.SIMPLE_MODE = true;

    const ps = this.points;
    const tree = this.sph.makeKDTree(ctx);

    let totpoint = 0;
    const lvl = ctx.SOLVE_LEVEL;

    for (let pi1 = 0; pi1 < this.points.length; pi1 += PTOT) {
      const gen = ps[pi1 + PGEN];

      totpoint += +(gen <= lvl);
    }

    if (totpoint == 0) {
      console.warn("Solver.leverlSPHStep(): No points!");
      return;
    }

    const r = 0.95 / Math.sqrt((ps.length / PTOT) * 0.05 + totpoint);
    const searchfac = ctx.SOLVER_SEARCHRAD;
    const speed = ctx.SOLVER_SPEED * 0.15;

    let sumdx = 0;
    let sumdy = 0;
    let sumw = 0;
    let sumtot = 0;
    let x1 = 0;
    let y1 = 0;
    let searchr = 0;
    let off: number[] = [0, 0];
    let pi1 = 0;

    const callback = (pi2: number) => {
      if (pi2 == pi1) return;

      const x2 = ps[pi2];
      const y2 = ps[pi2 + 1];

      const dx = -(x2 - x1 - off[0]);
      const dy = -(y2 - y1 - off[1]);
      let dis = dx * dx + dy * dy;

      if (dis == 0.0) {
        sumdx += dx;
        sumdy += dy;
        sumw += 1.0;
        sumtot += 1.0;

        return;
      }

      if (dis > searchr * searchr) {
        return;
      }

      dis = Math.sqrt(dis);

      let w = 1.0 - dis / searchr;
      w *= w * w;

      sumdx += dx * w;
      sumdy += dy * w;
      sumw += w;
      sumtot += 1;
    };

    for (pi1 = 0; pi1 < ps.length; pi1 += PTOT) {
      x1 = ps[pi1];
      y1 = ps[pi1 + 1];
      const gen1 = ps[pi1 + PGEN];

      if (gen1 > ctx.SOLVE_LEVEL) {
        continue;
      }

      sumdx = sumdy = sumw = sumtot = 0;
      searchr = r * searchfac;

      for (off of gridoffs) {
        tree.forEachPoint(x1 + off[0], y1 + off[1], searchr, callback);
      }

      if (sumw == 0.0) {
        continue;
      }

      sumdx /= sumw;
      sumdy /= sumw;

      ps[pi1] += sumdx * speed;
      ps[pi1 + 1] += sumdy * speed;

      ps[pi1] = Math.fract(ps[pi1]);
      ps[pi1 + 1] = Math.fract(ps[pi1 + 1]);
    }
  }

  makeLevelUniform(ctx: ConfigBag, lvl: number): void {
    ctx = ctx.copy();
    ctx.SOLVE_LEVEL = lvl;

    for (let i = 0; i < ctx.SOLVER_STEPS; i++) {
      this.levelSPHStep(ctx);
    }
  }
}

function* calcError(
  sph: SPH,
  ctx: ConfigBag,
  full_solve?: boolean,
  screenreport?: boolean
): Generator<number> {
  const slv = new Solver(sph);

  util.seed(cconst.SOLVER_RANDSEED);

  let sum = 0;

  if (full_solve) {
    let time = util.time_ms();

    const doreport = (i: number, ilen: number) => {
      if (util.time_ms() - time > 700) {
        console.log("doing", i + 1, "of", ilen);

        if (screenreport) {
          _appstate.report(`doing ${i + 1} of ${ilen}`);
        }

        time = util.time_ms();
      }
    };

    const report = (msg: string) => {
      console.log(msg);

      if (screenreport) {
        _appstate.report(msg);
      }
    };

    ctx = ctx.copy();

    //reset
    sph.reset(sph.dimen);

    //make sure we aren't in too random of a starting point
    for (let i = 0; i < 5; i++) {
      sph.step(ctx);
      yield 1;
    }

    const startps = sph.points.slice(0, sph.points.length);

    //figure out speed
    let speed = 0.1;
    const r = 0.95 / Math.sqrt(sph.points.length / PTOT);

    let lastgood = speed;
    let lastsum = 0;
    const isteps = 10;
    for (let i = 0; i < isteps; i++) {
      doreport(i, isteps);

      sph.points = startps.slice(0, startps.length);
      ctx.SPH_SPEED = speed;
      sph.step(ctx);

      const ps = sph.points;
      sum = 0.0;
      for (let pi = 0; pi < startps.length; pi += PTOT) {
        sum += torusPointDist(ps[pi], ps[pi + 1], startps[pi], startps[pi + 1]);
      }
      sum /= startps.length / PTOT;

      if (sum > r * 0.0425 * 1.5 * ctx.SOLVER_STARTSPEED_THRESH) {
        speed = lastgood;
        //sum = lastsum;
        break;
      }

      lastsum = sum;
      lastgood = speed;
      speed *= 1.525;

      yield 1;
    }
    void lastsum;

    sph.points = startps.slice(0, startps.length);

    report("Speed: " + speed.toFixed(5) + " Divergence: " + sum.toFixed(5) + " r: " + r.toFixed(5));

    window.shufflespeed = speed;

    for (let i = 0; i < ctx.SOLVER_NORMAL_STEPS; i++) {
      doreport(i, ctx.SOLVER_NORMAL_STEPS);

      sph.step(ctx);
      yield 1;
    }
  }

  yield slv.calcMaskError(ctx, sph.points);
}

//"optional" fract
function optfract(f: number): number {
  //return Math.fract(f);
  return f;
}

function calcradius(gen: number, maxgen: number, ctx: ConfigBag): number {
  const a = ctx.RADMUL / Math.sqrt(1 + maxgen);
  const b = a * ctx.MAX_SCALE;

  gen = Math.min(Math.max(gen, 0.0), 1.0) * 0.99999999;

  let f = 1.0 - gen;

  if (ctx.RADIUS_CURVE !== undefined && ctx.RADIUS_CURVE.evaluate !== undefined) {
    f = ctx.RADIUS_CURVE.evaluate(f);
  }

  return a + (b - a) * f;
}

class SPH {
  cmyk_tick: number;
  dimen: number;
  points: number[];
  totpoint: number;
  tick: number;
  r: number;
  totbase: number;
  maxgen: number;
  cur_r: number;
  cur_t: number;
  cur_t_i: number;
  kdtree: kdtree.KDTree | undefined;
  ct: string;

  constructor(dimen: number) {
    this.reset(dimen);
  }

  loadJSON(obj: { points: number[] }): this {
    this.points = obj.points;

    return this;
  }

  toJSON(): { points: number[]; dimen: number } {
    return {
      points: this.points,
      dimen: this.dimen,
    };
  }

  reset(dimen: number): void {
    util.seed(cconst.SOLVER_RANDSEED);

    this.cmyk_tick = 0;
    this.dimen = dimen;
    this.points = [];
    this.totpoint = 0;
    this.tick = 0;
    this.r = 0;
    this.totbase = 0.0;
    this.maxgen = 1.0;

    this.cur_r = 0;
    this.cur_t = (cconst.RANGE - 1) / cconst.RANGE;
    this.cur_t_i = cconst.RANGE - 1;

    this.throw();
  }

  throw(): void {
    const totpoint = Math.ceil(this.dimen * this.dimen * 0.95);
    const ps = this.points;

    const dimen2 = Math.floor(Math.sqrt(totpoint));
    this.r = undefined as unknown as number;

    for (let i = 0; i < totpoint; i++) {
      const ix = i % dimen2;
      const iy = ~~(i / dimen2);
      let x = ix / dimen2;
      let y = iy / dimen2;

      const rfac = cconst.RANDFAC;

      x += ((util.random() - 0.5) / dimen2) * rfac;
      y += ((util.random() - 0.5) / dimen2) * rfac;
      x = Math.fract(x);
      y = Math.fract(y);

      const pi = ps.length;
      for (let j = 0; j < PTOT; j++) {
        ps.push(0.0);
      }

      let gen = Math.fract(ix * 3.3234 + iy * 0.539 + util.random() * 0.05);

      gen = Math.max(gen - cconst.GENSTART, 0) / (1.0 - cconst.GENSTART);
      ps[pi + POGEN] = gen;

      gen = 1.0 - cconst.TONE_CURVE.evaluate(1.0 - gen);
      gen = Math.floor(gen * cconst.GENSTEPS) / cconst.GENSTEPS;

      ps[pi] = ps[pi + POLDX] = ps[pi + PSTARTX] = x;
      ps[pi + 1] = ps[pi + POLDY] = ps[pi + PSTARTY] = y;

      ps[pi + PGEN] = gen;
      const gi = i;

      ps[pi + PCLR] = gi % 4;
    }

    this.calcRadii(cconst);
  }

  makeKDTree(ctx: ConfigBag): kdtree.KDTree {
    const kd = (this.kdtree = new kdtree.KDTree([-2.5, -2.5, -2.5], [2.5, 2.5, 2.5]));

    const ps = this.points;
    const visit: Record<number, number> = {};
    let totpoint = 0;

    while (totpoint < ps.length / PTOT) {
      let pi = ~~((util.random() * 0.999999 * ps.length) / PTOT);
      if (pi in visit) {
        continue;
      }

      totpoint++;
      visit[pi] = 1;
      pi *= PTOT;

      if (ctx.SIMPLE_MODE && ps[pi + PGEN] > ctx.SOLVE_LEVEL) {
        continue;
      }

      kd.insert(ps[pi], ps[pi + 1], pi);
    }

    return kd;
  }

  updateVelocity(ctx?: ConfigBag): void {
    ctx = ctx === undefined ? cconst : ctx;

    const ps = this.points;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const fac = ctx.DV_DAMPING;
      let mindv = 1e17;

      //calc derivatives.  complicated by toroidal domain
      for (const off of gridoffs) {
        const dx2 = ps[pi] - (ps[pi + POLDX] + off[0]);
        const dy2 = ps[pi + 1] - (ps[pi + POLDY] + off[1]);
        const dis = dx2 * dx2 + dy2 * dy2;

        if (dis < mindv) {
          mindv = dis;
          ps[pi + PDX] = dx2;
          ps[pi + PDY] = dy2;
        }
      }

      ps[pi + PDX] *= fac;
      ps[pi + PDY] *= fac;

      ps[pi + POLDX] = ps[pi];
      ps[pi + POLDY] = ps[pi + 1];
    }
  }

  applyVelocity(ctx?: ConfigBag): void {
    ctx = ctx === undefined ? cconst : ctx;

    const ps = this.points;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      ps[pi] += ps[pi + PDX];
      ps[pi + 1] += ps[pi + PDY];

      ps[pi] = optfract(ps[pi]);
      ps[pi + 1] = optfract(ps[pi + 1]);
    }
  }

  jitter(): void {
    const ps = this.points;
    const size = this.r;

    for (let i = 0; i < ps.length; i += PTOT) {
      ps[i] += (util.random() - 0.5) * 2.0 * size;
      ps[i + 1] += (util.random() - 0.5) * 2.0 * size;

      ps[i] = Math.min(Math.max(ps[i], 0.0), 1.0);
      ps[i + 1] = Math.min(Math.max(ps[i + 1], 0.0), 1.0);
    }
  }

  calcRadii(ctx: ConfigBag): void {
    const ps = this.points;

    //cumulative distribution function (histogram) for
    //calculating point radius from modified (non-linear) gen threshold
    const cdf = new Float64Array(1024);

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const gen = ps[pi + PGEN];
      const ci = ~~(gen * cdf.length * 0.9999999);

      cdf[ci]++;
    }

    for (let i = 1; i < cdf.length; i++) {
      cdf[i] += cdf[i - 1];
    }

    const maxgen = cdf[cdf.length - 1];
    this.maxgen = maxgen;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const gen = ps[pi + PGEN];
      const ci = ~~(gen * cdf.length * 0.9999999);

      const r = calcradius(cdf[ci] / maxgen, maxgen, ctx);
      ps[pi + PR] = r;
    }

    this.r = this.cur_r = calcradius(1.0, maxgen, ctx);
  }

  shuffleGen(
    ctx: ConfigBag,
    randfac: number,
    full_solve?: boolean,
    screenreport?: boolean
  ): Generator<number> {
    randfac *= ctx.SOLVER_RANDFAC;

    const config = ctx.copy();
    const this2 = this;

    const do_all = !config.SOLVER_IN_SERIES;
    const params: Record<string, number> = {};

    if (ctx.SOLVER_PARAM_WEIGHT != 0) {
      for (let i = ctx.SOLVER_PARAM_MIN; i <= ctx.SOLVER_PARAM_MAX; i++) {
        if (i == 5) continue;

        const key = "PARAM" + i;
        params[key] = ctx.SOLVER_PARAM_WEIGHT;
      }
    }

    if (ctx.SOLVER_SEARCHRAD_WEIGHT != 0) {
      params["SEARCHRAD"] = ctx.SOLVER_SEARCHRAD_WEIGHT;
    }

    if (ctx.SOLVER_MAXSCALE_WEIGHT != 0) {
      params["MAX_SCALE"] = ctx.SOLVER_MAXSCALE_WEIGHT;
    }

    const list = [];
    for (const k in params) {
      list.push(k);
    }

    if (do_all) {
      for (let i = 0; i < list.length; i++) {
        const k = list[i];
        const v = params[k];

        config[k] += (Math.random() - 0.5) * randfac * v;
        config[k] = Math.max(config[k], 0.000001);
      }
    } else {
      const ri = ~~(Math.random() * list.length * 0.999999);

      //do both of these together
      if (list[ri] == "SEARCHRAD" || list[ri] == "MAX_SCALE") {
        config.SEARCHRAD += (Math.random() - 0.5) * randfac * config.SOLVER_SEARCHRAD_WEIGHT;
        config.MAX_SCALE += (Math.random() - 0.5) * randfac * config.SOLVER_MAXSCALE_WEIGHT;

        config.SEARCHRAD = Math.max(config.SEARCHRAD, 0.25);
        config.MAX_SCALE = Math.max(config.MAX_SCALE, 0.25);
      } else {
        const k = list[ri];
        const v = params[k];

        config[k] += (Math.random() - 0.5) * randfac * v;
        config[k] = Math.max(config[k], 0.0);
      }
    }

    window.shuffleconfig = config.copy();

    return (function* () {
      for (const val of this2.calcErrorGen(config, full_solve, screenreport)) {
        yield val;
      }
    })();
  }

  calcErrorGen(ctx?: ConfigBag, full_solve?: boolean, screenreport?: boolean): Generator<number> {
    ctx = ctx === undefined ? cconst : ctx;

    return calcError(this, ctx, full_solve, screenreport);
  }

  calcError(ctx?: ConfigBag, full_solve?: boolean, screenreport?: boolean): number | undefined {
    ctx = ctx === undefined ? cconst : ctx;
    let val;

    for (val of calcError(this, ctx, full_solve, screenreport)) {
      // drive the generator to completion; last yielded value is the result
    }

    return val;
  }

  step(ctx: ConfigBag = cconst): void {
    this.cmyk_tick++;

    const ps = this.points;
    const searchfac = ctx.SEARCHRAD;
    let cmyk_searchfac = ctx.CMYK_SEARCHRAD;

    const ct = Math.min(this.cmyk_tick / 64, 1.0);

    cmyk_searchfac += (0.6 - cmyk_searchfac) * (1.0 - ct);

    this.ct = cmyk_searchfac.toFixed(3);

    let x1 = 0;
    let y1 = 0;
    let r1 = 0;
    let sumdx = 0;
    let sumdy = 0;
    let sumw = 0;
    let sumtot = 0;
    let searchr = 0;
    let gen1 = 0;
    let off: number[] = [0, 0];
    let pi1 = 0;
    let color1 = 0;
    let sumcolorw = 0;
    let sumcolortot = 0;
    const sumcmyk = [0, 0, 0, 0];
    let sumcolor = 0;

    let max_r: number | undefined = undefined;

    ctx = ctx.copy();
    ctx.SIMPLE_MODE = false;
    ctx.ADV_SOLVE = false;

    this.calcRadii(ctx);

    const tree = this.makeKDTree(ctx);
    const tot = ps.length / PTOT;

    if (tot == 0) {
      return;
    }

    const calcweight = (w: number, r1: number, r2: number, gen1: number, gen2: number): number => {
      let g1 = gen1 - gen2 + 1.0;
      let g2 = (gen2 + 0.0001) / (gen1 + 0.0001);

      g1 = Math.pow(g1, ctx!.PARAM1);
      g2 = Math.pow(g2, ctx!.PARAM4);

      w = Math.pow(w, g2 * ctx!.PARAM2 + ctx!.PARAM3);
      w *= g1 * ctx!.PARAM5 + 1.0 - ctx!.PARAM5;

      if (gen1 < gen2) {
        w *= ctx!.PARAM6;
        w = Math.pow(w, ctx!.PARAM7);
      }
      return w;
    };

    const callback = (pi2: number) => {
      if (pi1 == pi2) {
        return;
      }

      const x2 = ps[pi2];
      const y2 = ps[pi2 + 1];
      const r2 = ps[pi2 + PR];
      const gen2 = ps[pi2 + PGEN];

      let dx = x2 - x1 - off[0];
      let dy = y2 - y1 - off[1];

      let dis = dx * dx + dy * dy;

      if (dis == 0.0 || dis >= searchr * searchr) {
        return;
      }

      dis = dis != 0.0 ? Math.sqrt(dis) : 0.0;
      let w = 1.0 - dis / searchr;

      w = calcweight(w, r1, r2, gen1, gen2);

      dx /= dis;
      dy /= dis;

      sumdx += dx * w;
      sumdy += dy * w;
      sumw += w;
      sumtot += 1.0;
    };

    const cmyk_callback = (pi2: number) => {
      if (pi1 == pi2) {
        return;
      }

      const x2 = ps[pi2];
      const y2 = ps[pi2 + 1];
      const r2 = ps[pi2 + PR];
      const gen2 = ps[pi2 + PGEN];
      const color2 = ps[pi2 + PCLR];

      const dx = x2 - x1 - off[0];
      const dy = y2 - y1 - off[1];

      let dis = dx * dx + dy * dy;

      if (dis == 0.0 || dis >= searchr * searchr) {
        return;
      }

      dis = dis != 0.0 ? Math.sqrt(dis) : 0.0;
      let w = 1.0 - dis / searchr;

      w = calcweight(w, r1, r2, gen1, gen2);
      w *= w * w;

      sumcolor += color2 * w;
      sumcmyk[color2] += w;

      sumcolorw += w;
      sumcolortot += 1.0;
    };

    const fac = ctx.SPH_SPEED * 0.5;

    for (let pi = 0; pi < ps.length; pi += PTOT) {
      const r1b = ps[pi + PR];

      max_r = max_r === undefined ? r1b : Math.max(max_r, r1b);
    }

    const swaps: number[][] = [[], [], [], []];
    const bins: number[][] = [[], [], [], []];

    for (pi1 = 0; pi1 < ps.length; pi1 += PTOT) {
      sumdx = sumdy = sumw = sumtot = sumcolor = sumcolorw = sumcolortot = 0.0;

      for (let i = 0; i < 4; i++) {
        sumcmyk[i] = 0;
      }

      x1 = ps[pi1];
      y1 = ps[pi1 + 1];
      r1 = ps[pi1 + PR];
      gen1 = ps[pi1 + PGEN];
      color1 = ps[pi1 + PCLR];

      for (off of gridoffs) {
        searchr = r1 * searchfac;
        tree.forEachPoint(x1 + off[0], y1 + off[1], searchr, callback);

        searchr = r1 * cmyk_searchfac;
        tree.forEachPoint(x1 + off[0], y1 + off[1], searchr, cmyk_callback);
      }

      if (sumtot == 0.0 || sumw == 0.0) {
        continue;
      }

      sumdx /= sumw;
      sumdy /= sumw;

      let fac2 = Math.pow(1.0 - r1 / max_r!, 2.0) + 0.125;
      fac2 *= 0.5;
      fac2 = 0.1;

      ps[pi1] += -sumdx * fac2 * fac;
      ps[pi1 + 1] += -sumdy * fac2 * fac;

      if (sumcolorw == 0.0) {
        continue;
      }

      if (sumcolor / sumcolorw >= 4) {
        throw new Error("sumcolor corruption");
      }

      sumcolor = ~((sumcolor + color1) / (sumcolorw + 1)) % 4;

      let mini = 0;
      let maxi = 0;
      let minc = 0;
      let maxc = 0;

      for (let i = 0; i < 4; i++) {
        sumcmyk[i] /= sumcolortot;

        if (i == 0 || sumcmyk[i] > maxc) {
          maxc = sumcmyk[i];
          maxi = i;
        }

        if (i == 0 || sumcmyk[i] < minc) {
          minc = sumcmyk[i];
          mini = i;
        }
      }
      void maxi;

      if (mini != color1) {
        swaps[mini].push(pi1);
        bins[mini].push(pi1);
      }
    }

    for (let i = 0; i < 4; i++) {
      swaps[i].sort();
      bins[i].sort();
    }

    const steplen = swaps[0].length + swaps[1].length + swaps[2].length + swaps[3].length;
    for (let step = 0; step < steplen; step++) {
      const i = ~~(util.random() * 3.999999);

      for (let i2 = 0; i2 < swaps[i].length; i2++) {
        const pi1b = swaps[i][i2];
        const rj = ~~(util.random() * 2.999999);
        let j = (i + rj) % 4;

        j = ps[pi1b + PCLR];

        if (j == undefined) {
          throw new Error("eek!");
        }

        if (swaps[j].length == 0) {
          continue;
        }

        let k;
        for (k = 0; k < swaps[j].length; k++) {
          if (ps[swaps[j][k] + PCLR] == i) {
            break;
          }
        }

        if (k == swaps[j].length) {
          continue;
        }

        const pi2 = swaps[j][k];

        ps[pi1b + PCLR] = i;
        ps[pi2 + PCLR] = j;

        //remove from lists
        swaps[i][i2] = swaps[i][swaps[i].length - 1];
        swaps[j][k] = swaps[j][swaps[j].length - 1];

        swaps[i].pop();
        swaps[j].pop();

        i2--;
      }
    }

    for (pi1 = 0; pi1 < ps.length; pi1 += PTOT) {
      ps[pi1] = Math.fract(ps[pi1]);
      ps[pi1 + 1] = Math.fract(ps[pi1 + 1]);

      ps[pi1 + PSTARTX] = ps[pi1];
      ps[pi1 + PSTARTY] = ps[pi1 + 1];
    }
  }
}

export { Solver, SPH };
